export const PROGRAM_APPLICATION_POPUP_ID = 'program-application' as const;
export const SCHOOL_NEWSLETTER_POPUP_ID = 'school-newsletter' as const;
export const VIDEO_POPUP_ID = 'video-modal' as const;

export type PopupId =
    | typeof PROGRAM_APPLICATION_POPUP_ID
    | typeof SCHOOL_NEWSLETTER_POPUP_ID
    | typeof VIDEO_POPUP_ID
    | (string & {});

export interface PopupRegistration {
    onClose?: () => void;
}

export interface PopupStore {
    activeId: PopupId | null;
    displayId: PopupId | null;
    payload: Record<string, unknown> | null;
    openModal(id: string): void;
    closeModal(id?: string): void;
    register(id: PopupId, registration: PopupRegistration): void;
    unregister(id: PopupId): void;
    isOpen(id: PopupId): boolean;
    open(id: PopupId, payload?: Record<string, unknown> | null): void;
    close(id?: PopupId): void;
    closeAll(): void;
}

let originalBodyPaddingRight: string | null = null;
let closeDisplayTimer: number | null = null;

const readScrollbarWidth = (): string => {
    const cssWidth = window
        .getComputedStyle(document.documentElement)
        .getPropertyValue('--uu-scrollbar-width')
        .trim();

    if (cssWidth) {
        return cssWidth;
    }

    return `${window.innerWidth - document.body.clientWidth}px`;
};

const syncBodyState = (activeId: PopupId | null): void => {
    if (typeof document === 'undefined') {
        return;
    }

    const { body } = document;

    if (activeId !== null) {
        if (originalBodyPaddingRight === null) {
            originalBodyPaddingRight = body.style.paddingRight;
        }

        const scrollbarWidth = readScrollbarWidth();

        body.style.paddingRight = scrollbarWidth === '0px'
            ? originalBodyPaddingRight || ''
            : scrollbarWidth;
    } else if (originalBodyPaddingRight !== null) {
        body.style.paddingRight = originalBodyPaddingRight;
        originalBodyPaddingRight = null;
    }

    body.classList.toggle('overflow-hidden', activeId !== null);
};

let popupStore: PopupStore | null = null;

type FormsStore = {
    submit?: (id: string) => Promise<unknown>;
};

const readAlpinePopupStore = (): PopupStore | null => {
    if (typeof window === 'undefined' || !window.Alpine) {
        return null;
    }

    return window.Alpine.store('popup') as PopupStore;
};

const readAlpineFormsStore = (): FormsStore | null => {
    if (typeof window === 'undefined' || !window.Alpine) {
        return null;
    }

    return window.Alpine.store('forms') as FormsStore;
};

export const createPopupStore = (): PopupStore => {
    if (popupStore) {
        return popupStore;
    }

    const registrations = new Map<PopupId, PopupRegistration>();

    popupStore = {
        activeId: null,
        displayId: null,
        payload: null,
        openModal(id: string): void {
            getPopupStore().open(id);
        },
        closeModal(id?: string): void {
            getPopupStore().close(id);
        },
        register(id, registration) {
            registrations.set(id, registration);
        },
        unregister(id) {
            registrations.delete(id);
        },
        isOpen(id) {
            return this.activeId === id;
        },
        open(id, payload = null) {
            if (closeDisplayTimer !== null) {
                window.clearTimeout(closeDisplayTimer);
                closeDisplayTimer = null;
            }

            if (this.activeId) {
                registrations.get(this.activeId)?.onClose?.();
            }

            this.activeId = id;
            this.displayId = id;
            this.payload = payload;
            syncBodyState(this.activeId);
        },
        close(id) {
            if (!this.activeId) {
                syncBodyState(null);
                return;
            }

            if (id && this.activeId !== id) {
                return;
            }

            const closingId = this.activeId;

            this.activeId = null;
            this.payload = null;
            registrations.get(closingId)?.onClose?.();
            syncBodyState(this.activeId);

            if (closeDisplayTimer !== null) {
                window.clearTimeout(closeDisplayTimer);
            }

            closeDisplayTimer = window.setTimeout(() => {
                const store = readAlpinePopupStore() ?? popupStore;

                if (store && store.activeId === null) {
                    store.displayId = null;
                }

                closeDisplayTimer = null;
            }, 200);
        },
        closeAll() {
            this.close();
        },
    };

    return popupStore;
};

export const getPopupStore = (): PopupStore => readAlpinePopupStore() ?? createPopupStore();

export const createPopupRoot = () => ({
    openModal(id: PopupId = PROGRAM_APPLICATION_POPUP_ID) {
        getPopupStore().open(id);
    },
    closeModal() {
        getPopupStore().close();
    },
    submitApplication() {
        return readAlpineFormsStore()?.submit?.('application-form');
    },
    submitNewsletter() {
        return readAlpineFormsStore()?.submit?.('newsletter-form');
    },
    handleEscape(event: KeyboardEvent) {
        const store = getPopupStore();

        if (!store.activeId) {
            return;
        }

        event.preventDefault();
        store.closeAll();
    },
});